import { useState, useRef, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DispatchPhoto, PhotoLightbox } from "@/components/DispatchPhoto";
import { PdfViewer } from "@/components/PdfViewer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PushBell } from "@/components/PushBell";
import {
  Truck, Search, Loader2, Download, Eye, MapPin, PackageSearch, Store, CalendarDays,
  Plus, Trash2, Camera, ListChecks, ChevronLeft, ChevronRight, Package, CheckCircle2, User, Phone, AlertTriangle, X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import {
  format, parseISO, isToday, isTomorrow, isPast, startOfDay, addDays,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  isSameMonth, isSameDay, addMonths, subMonths,
} from "date-fns";

const STATUS_FLOW = ["pending", "packed", "dispatched"];
const STATUS_META = {
  pending: { label: "Pending", icon: ListChecks, cls: "bg-[#D97706]/12 text-[#D97706]", solid: "bg-[#D97706]" },
  packed: { label: "Packed", icon: Package, cls: "bg-[#0284C7]/12 text-[#0284C7]", solid: "bg-[#0284C7]" },
  dispatched: { label: "Dispatched", icon: CheckCircle2, cls: "bg-[#059669]/12 text-[#059669]", solid: "bg-[#059669]" },
};

// Instant, all-field dispatch search. When terms are present, every term must appear somewhere
// across the order #, builder/company, contact, delivery address, pickup person/mobile, warehouse
// note, added-by, status/type/handling/date AND every line-item name + SKU. Returns "matched in"
// hints so a rep can see WHY a card surfaced (e.g. a tile SKU or a note).
// Wraps matched search terms in each result card with a subtle highlight so reps instantly
// see WHY a card surfaced. Escapes regex specials; matches are case-insensitive.
function Highlight({ text, terms }) {
  const s = String(text ?? "");
  if (!terms?.length || !s) return s;
  const esc = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).filter(Boolean);
  if (!esc.length) return s;
  const re = new RegExp(`(${esc.join("|")})`, "ig");
  const lower = new Set(terms.map((t) => t.toLowerCase()));
  return s.split(re).map((p, i) =>
    p && lower.has(p.toLowerCase())
      ? <mark key={i} className="rounded-[3px] bg-[#FDE68A] px-0.5 text-ink">{p}</mark>
      : <span key={i}>{p}</span>
  );
}

function matchDispatch(d, terms) {
  const labeled = [
    ["Order #", d.order_number],
    ["Builder", d.company],
    ["Contact", d.contact_name],
    ["Address", d.delivery_address],
    ["Pickup", d.pickup_name],
    ["Mobile", d.pickup_mobile],
    ["Note", d.warehouse_note],
    ["Added by", d.created_by_name],
  ].filter(([, v]) => v);
  const items = (d.items || [])
    .map((it) => `${it.inflow_product_name || it.name || ""} ${it.sku || ""}`.trim())
    .filter(Boolean);
  const metas = [d.status, d.fulfil_type, d.handling, d.fulfil_date].filter(Boolean);
  if (!terms.length) return { d, matched: true, hints: [] };
  const hay = [...labeled.map(([, v]) => v), ...items, ...metas].join(" \u0001 ").toLowerCase();
  if (!terms.every((t) => hay.includes(t))) return { d, matched: false, hints: [] };
  const hints = [];
  for (const [label, v] of labeled) {
    if (terms.some((t) => String(v).toLowerCase().includes(t))) hints.push({ label, text: String(v) });
  }
  const itemHit = items.find((s) => terms.some((t) => s.toLowerCase().includes(t)));
  if (itemHit) hints.push({ label: "Items", text: itemHit });
  return { d, matched: true, hints };
}

function StatusStepper({ dispatch, onChange }) {
  const idx = STATUS_FLOW.indexOf(dispatch.status);
  return (
    <div className="inline-flex flex-wrap items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1" data-testid={`dispatch-status-${dispatch.id}`}>
      {STATUS_FLOW.map((s, i) => {
        const M = STATUS_META[s];
        const active = i === idx;
        const done = i <= idx;
        return (
          <button key={s} onClick={() => onChange(dispatch, s)} data-testid={`set-status-${s}-${dispatch.id}`} title={`Mark ${M.label}`}
            className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
              active ? cn(M.solid, "text-white shadow-sm") : done ? M.cls : "text-slate-500 hover:bg-white hover:text-ink hover:shadow-sm")}>
            <M.icon className="h-4 w-4" />{M.label}
          </button>
        );
      })}
    </div>
  );
}

function isOverdueDispatch(d) {
  try { const dt = parseISO(d.fulfil_date); return isPast(startOfDay(dt)) && !isToday(dt) && d.status !== "dispatched"; }
  catch { return false; }
}

function DispatchCard({ d, onStatus, onDelete, onPdf, onView, onOpenPhotos, canManage, busyPdf, terms = [] }) {
  const isPickup = d.fulfil_type === "pickup";
  const overdue = isOverdueDispatch(d);
  const photoCount = d.photos?.length || 0;
  return (
    <div data-testid={`dispatch-card-${d.id}`} onClick={() => onOpenPhotos(d)} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpenPhotos(d); }}
      className="cursor-pointer rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:border-primary/30 hover:shadow-[0_4px_18px_rgba(35,52,90,0.10)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent px-2.5 py-0.5 font-mono text-xs font-bold text-accent-foreground"><Highlight text={d.order_number} terms={terms} /></span>
            <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", isPickup ? "bg-[#7C3AED]/12 text-[#7C3AED]" : "bg-primary/10 text-primary")}>
              {isPickup ? <Store className="h-3 w-3" /> : <Truck className="h-3 w-3" />}{isPickup ? "Pickup" : "Delivery"}
            </span>
            {overdue && <span data-testid={`overdue-badge-${d.id}`} className="inline-flex items-center gap-1 rounded-full bg-error/10 px-2 py-0.5 text-[11px] font-semibold text-error">Overdue</span>}
            <span className="text-xs text-muted-foreground">{d.item_count} item(s)</span>
            {d.handling && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-ink" data-testid={`dispatch-handling-badge-${d.id}`}><Package className="h-3 w-3" />{d.handling === "pallet" ? "Pallet" : "Hand loaded"}</span>}
          </div>
          {d.company && <div className="mt-1.5 font-display text-sm font-bold text-ink"><Highlight text={d.company} terms={terms} /></div>}
          {isPickup ? (
            (d.pickup_name || d.pickup_mobile) && (
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                {d.pickup_name && <span className="flex items-center gap-1"><User className="h-3 w-3" /><Highlight text={d.pickup_name} terms={terms} /></span>}
                {d.pickup_mobile && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /><Highlight text={d.pickup_mobile} terms={terms} /></span>}
              </div>
            )
          ) : (
            d.delivery_address && <div className="mt-0.5 flex items-start gap-1 text-xs text-muted-foreground"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span><Highlight text={d.delivery_address} terms={terms} /></span></div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={(e) => { e.stopPropagation(); onView(d); }} size="sm" data-testid={`dispatch-view-${d.id}`} className="h-9 gap-1.5 text-sm shadow-sm"><Eye className="h-4 w-4" />View</Button>
          <Button onClick={(e) => { e.stopPropagation(); onPdf(d); }} disabled={busyPdf} size="sm" variant="outline" data-testid={`dispatch-pdf-${d.id}`} className="h-9 gap-1.5 border-slate-300 text-sm">
            {busyPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}PDF
          </Button>
          {canManage && (
            <button onClick={(e) => { e.stopPropagation(); onDelete(d); }} data-testid={`delete-dispatch-${d.id}`} title="Remove dispatch" className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-muted-foreground transition-colors hover:border-error/40 hover:bg-error/10 hover:text-error">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 border-t border-slate-100 pt-3" onClick={(e) => e.stopPropagation()}>
        <StatusStepper dispatch={d} onChange={onStatus} />
      </div>

      <div data-testid={`open-dispatch-photos-${d.id}`}
        className="mt-3 flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5 text-sm">
        <span className="flex items-center gap-2 font-semibold text-ink">
          <Camera className="h-4 w-4 text-primary" />
          {photoCount ? `${photoCount} photo${photoCount > 1 ? "s" : ""}` : "Add / view photos"}
        </span>
        <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
          {photoCount ? "Open" : "No photos yet"}<ChevronRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

// Cross-device date picker. On touch devices (notably iOS standalone PWA, where a Radix
// Popover+Calendar inside a Dialog doesn't register taps) we overlay a NATIVE <input type="date">
// so the reliable OS picker opens. Desktop keeps the styled calendar popover.
function DateField({ value, onChange, placeholder = "Pick a date", testid, className }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const coarse = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  if (coarse) {
    return (
      <div className={cn("relative", className)}>
        <div className={cn("flex h-10 items-center gap-2 rounded-md border border-input bg-white px-3 text-sm font-normal", !value && "text-muted-foreground")}>
          <CalendarDays className="h-4 w-4 shrink-0" />
          <span>{value ? format(selected, "EEE, d MMM yyyy") : placeholder}</span>
        </div>
        <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          aria-label={placeholder} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
      </div>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testid}
          className={cn("justify-start gap-2 bg-white font-normal", !value && "text-muted-foreground", className)}>
          <CalendarDays className="h-4 w-4 shrink-0" />
          {value ? format(selected, "EEE, d MMM yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[110] w-auto p-0" align="start">
        <Calendar mode="single" selected={selected} defaultMonth={selected} initialFocus
          onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }} />
      </PopoverContent>
    </Popover>
  );
}

const DELIVERY_METHODS = [["needafork", "NeedAFork"], ["van", "Van"], ["other", "Other"]];
export const deliveryMethodLabel = (m, o) => m === "other" ? ((o || "").trim() || "Other") : (DELIVERY_METHODS.find((x) => x[0] === m)?.[1] || "");
function MethodSelect({ value, other, onValue, onOther, idp = "" }) {
  return (
    <div className="mt-4" data-testid={`${idp}method-block`}>
      <Label className="mb-1.5 flex items-center gap-1.5 text-xs"><Truck className="h-3.5 w-3.5" />Delivery method <span className="text-error">*</span></Label>
      <div className="inline-flex flex-wrap gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5" data-testid={`${idp}method-toggle`}>
        {DELIVERY_METHODS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => onValue(v)} data-testid={`${idp}method-${v}`}
            className={cn("rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", value === v ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}>{l}</button>
        ))}
      </div>
      {value === "other" && <Input value={other || ""} onChange={(e) => onOther(e.target.value)} placeholder="Specify delivery method" className="mt-2 bg-white" data-testid={`${idp}method-other-input`} />}
    </div>
  );
}

function DispatchDetailDialog({ dispatch, onClose, onStatus, onUpload, onDeletePhoto, onSave, canManage, uploading }) {
  const fileRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [ftype, setFtype] = useState("delivery");
  const [fdate, setFdate] = useState("");
  const [addr, setAddr] = useState("");
  const [pname, setPname] = useState("");
  const [pmob, setPmob] = useState("");
  const [hand, setHand] = useState("hand");
  const [wnote, setWnote] = useState("");
  const [dmethod, setDmethod] = useState("");
  const [dother, setDother] = useState("");
  const [dcname, setDcname] = useState("");
  const [dcmob, setDcmob] = useState("");
  const [dnote, setDnote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (dispatch) {
      setFtype(dispatch.fulfil_type || "delivery");
      setFdate(dispatch.fulfil_date || "");
      setAddr(dispatch.delivery_address || "");
      setPname(dispatch.pickup_name || "");
      setPmob(dispatch.pickup_mobile || "");
      setHand(dispatch.handling || "hand");
      setWnote(dispatch.warehouse_note || "");
      setDmethod(dispatch.delivery_method || "");
      setDother(dispatch.delivery_method_other || "");
      setDcname(dispatch.delivery_contact_name || "");
      setDcmob(dispatch.delivery_contact_mobile || "");
      setDnote(dispatch.delivery_note || "");
    }
  }, [dispatch?.id]);
  const save = async () => {
    if (!fdate) { toast.error("Pick a date first"); return; }
    if (ftype === "delivery") {
      if (!dmethod) { toast.error("Pick a delivery method"); return; }
      if (dmethod === "other" && !dother.trim()) { toast.error("Specify the 'Other' method"); return; }
    }
    setSaving(true);
    const fields = ftype === "pickup"
      ? { fulfil_type: "pickup", fulfil_date: fdate, pickup_name: pname, pickup_mobile: pmob, handling: hand, warehouse_note: wnote, delivery_method: null, delivery_method_other: null }
      : { fulfil_type: "delivery", fulfil_date: fdate, delivery_address: addr, handling: hand, warehouse_note: wnote, delivery_method: dmethod, delivery_method_other: dmethod === "other" ? dother : null, delivery_contact_name: dcname, delivery_contact_mobile: dcmob, delivery_note: dnote };
    await onSave(dispatch, fields);
    setSaving(false);
  };
  const d = dispatch;
  const isPickup = d?.fulfil_type === "pickup";
  return (
    <>
      <Dialog open={!!dispatch} onOpenChange={(o) => { if (!o) { setLightbox(null); onClose(); } }}>
        <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="dispatch-detail-dialog">
          {d && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent px-2.5 py-0.5 font-mono text-sm font-bold text-accent-foreground">{d.order_number}</span>
                  <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", isPickup ? "bg-[#7C3AED]/12 text-[#7C3AED]" : "bg-primary/10 text-primary")}>
                    {isPickup ? <Store className="h-3 w-3" /> : <Truck className="h-3 w-3" />}{isPickup ? "Pickup" : "Delivery"}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  {d.company && <div className="font-display text-base font-bold text-ink">{d.company}</div>}
                  <div>{d.item_count} item(s)</div>
                </div>

                {canManage ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="dispatch-edit-fulfil">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-ink"><CalendarDays className="h-3.5 w-3.5" />Fulfilment</div>
                    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="edit-fulfil-toggle">
                      <button type="button" onClick={() => setFtype("delivery")} data-testid="edit-type-delivery"
                        className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", ftype === "delivery" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}><Truck className="h-3.5 w-3.5" />Delivery</button>
                      <button type="button" onClick={() => setFtype("pickup")} data-testid="edit-type-pickup"
                        className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", ftype === "pickup" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}><Store className="h-3.5 w-3.5" />Pickup</button>
                    </div>
                    <div className="mt-2">
                      <Label className="mb-1 block text-xs">{ftype === "pickup" ? "Pickup date" : "Delivery date"}</Label>
                      <DateField value={fdate} onChange={setFdate} testid="edit-fulfil-date" className="w-full" />
                    </div>
                    {ftype === "delivery" && <MethodSelect value={dmethod} other={dother} onValue={setDmethod} onOther={setDother} idp="edit-" />}
                    <div className="mt-2">
                      <Label className="mb-1 block text-xs">Handling for warehouse</Label>
                      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="edit-handling-toggle">
                        <button type="button" onClick={() => setHand("hand")} data-testid="edit-handling-hand" className={cn("rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", hand === "hand" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}>Hand loaded</button>
                        <button type="button" onClick={() => setHand("pallet")} data-testid="edit-handling-pallet" className={cn("rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", hand === "pallet" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}>Pallet</button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="mb-1 block text-xs">Note for warehouse picking team</Label>
                      <Textarea value={wnote} onChange={(e) => setWnote(e.target.value)} placeholder="e.g. Fragile — double-box tapware. Customer collecting 2pm." data-testid="edit-warehouse-note" className="min-h-[60px] bg-white" />
                      <p className="mt-1 text-[11px] text-muted-foreground">Shows as a highlighted box on the pick sheet PDF.</p>
                    </div>
                    {ftype === "pickup" ? (
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <div><Label className="mb-1 block text-xs">Pickup name</Label><Input value={pname} onChange={(e) => setPname(e.target.value)} data-testid="edit-pickup-name" className="bg-white" /></div>
                        <div><Label className="mb-1 block text-xs">Pickup mobile</Label><Input value={pmob} onChange={(e) => setPmob(e.target.value)} inputMode="tel" data-testid="edit-pickup-mobile" className="bg-white" /></div>
                      </div>
                    ) : (
                      <>
                        <div className="mt-2"><Label className="mb-1 block text-xs">Delivery address</Label><Input value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="edit-address" className="bg-white" /></div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <div><Label className="mb-1 block text-xs">Customer name <span className="text-muted-foreground">(optional)</span></Label><Input value={dcname} onChange={(e) => setDcname(e.target.value)} data-testid="edit-delivery-name" className="bg-white" /></div>
                          <div><Label className="mb-1 block text-xs">Customer number <span className="text-muted-foreground">(optional)</span></Label><Input value={dcmob} onChange={(e) => setDcmob(e.target.value)} inputMode="tel" data-testid="edit-delivery-mobile" className="bg-white" /></div>
                        </div>
                        <div className="mt-2"><Label className="mb-1 block text-xs">Delivery note <span className="text-muted-foreground">(optional)</span></Label><Textarea value={dnote} onChange={(e) => setDnote(e.target.value)} placeholder="e.g. Call on arrival, gate code 1234." data-testid="edit-delivery-note" className="min-h-[56px] bg-white" /><p className="mt-1 text-[11px] text-muted-foreground">Shown on the shared delivery link.</p></div>
                      </>
                    )}
                    <div className="mt-3 flex justify-end">
                      <Button onClick={save} disabled={saving} size="sm" data-testid="save-dispatch-btn" className="gap-1.5">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarDays className="h-3.5 w-3.5" />}Save changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    <div className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{isPickup ? "Pickup" : "Delivery"}: {d.fulfil_date || "Not set"}</div>
                    {isPickup ? (
                      (d.pickup_name || d.pickup_mobile) && (
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          {d.pickup_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{d.pickup_name}</span>}
                          {d.pickup_mobile && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{d.pickup_mobile}</span>}
                        </div>
                      )
                    ) : (
                      d.delivery_address && <div className="mt-1 flex items-start gap-1"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{d.delivery_address}</div>
                    )}
                    {!isPickup && (d.delivery_contact_name || d.delivery_contact_mobile || d.delivery_note) && (
                      <div className="mt-1 space-y-0.5">
                        {(d.delivery_contact_name || d.delivery_contact_mobile) && (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            {d.delivery_contact_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{d.delivery_contact_name}</span>}
                            {d.delivery_contact_mobile && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{d.delivery_contact_mobile}</span>}
                          </div>
                        )}
                        {d.delivery_note && <div className="flex items-start gap-1"><Package className="mt-0.5 h-3 w-3 shrink-0" />{d.delivery_note}</div>}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <div className="mb-2 text-xs font-semibold text-ink">Status</div>
                  <StatusStepper dispatch={d} onChange={onStatus} />
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-ink"><Camera className="h-3.5 w-3.5" />Dispatch photos {d.photos?.length ? `(${d.photos.length})` : ""}</span>
                    <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid={`photo-input-${d.id}`} onChange={(e) => { onUpload(d, e.target.files); e.target.value = ""; }} />
                    <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" variant="outline" data-testid={`add-photo-${d.id}`} className="h-8 gap-1.5 border-primary/30 text-xs text-primary hover:bg-accent">
                      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}Add photos
                    </Button>
                  </div>
                  {d.photos?.length ? (
                    <div className="flex flex-wrap gap-2" data-testid={`dispatch-photos-${d.id}`}>
                      {d.photos.map((p, i) => <DispatchPhoto key={p.id} dispatchId={d.id} photo={p} onOpen={() => setLightbox(i)} onDelete={canManage ? (ph) => onDeletePhoto(d, ph) : undefined} />)}
                    </div>
                  ) : (
                    <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-3 py-4 text-center text-xs text-muted-foreground">
                      No photos yet. The dispatch team can upload pictures of what was sent for your records.
                    </p>
                  )}
                </div>
              </div>
              {lightbox !== null && d.photos?.length ? (
                <PhotoLightbox dispatchId={d.id} photos={d.photos} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} />
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function BoardColumn({ title, Icon, items, headerCls, emptyText, cardProps, pdfBusy, uploadBusy, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-100 bg-slate-50/40 p-3">
      <div className={cn("mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold", headerCls)}>
        <Icon className="h-4 w-4" />{title}
        <span className="ml-auto rounded-full bg-white/70 px-2 py-0.5 text-xs font-bold">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="px-2 py-8 text-center text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="space-y-3">
          {items.map((d) => <DispatchCard key={d.id} d={d} {...cardProps} busyPdf={pdfBusy === d.id} uploading={uploadBusy === d.id} />)}
        </div>
      )}
    </div>
  );
}

export default function Dispatch() {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAdd = user?.role === "admin" || user?.role === "rep";
  const [tab, setTab] = useState(() => (user?.role === "warehouse" ? "today" : "list"));
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [query, setQuery] = useState("");
  const [needsOpen, setNeedsOpen] = useState(false);
  const needs = useQuery({ queryKey: ["dispatch-needs-scheduling"], queryFn: async () => (await client.get("/dispatch/needs-scheduling")).data, enabled: canAdd });

  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [data, setData] = useState(null);
  const [address, setAddress] = useState("");
  const [pickupName, setPickupName] = useState("");
  const [pickupMobile, setPickupMobile] = useState("");
  const [deliveryContactName, setDeliveryContactName] = useState("");
  const [deliveryContactMobile, setDeliveryContactMobile] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [fulfilType, setFulfilType] = useState("delivery");
  const [fulfilDate, setFulfilDate] = useState("");
  const [handling, setHandling] = useState("hand");
  const [whNote, setWhNote] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [deliveryMethodOther, setDeliveryMethodOther] = useState("");

  const [pdfBusy, setPdfBusy] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(null);
  const [pdfView, setPdfView] = useState(null); // { blob, name, dispatch }

  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(new Date());
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({ queryKey: ["dispatches"], queryFn: async () => (await client.get("/dispatch")).data });
  const dispatches = list.data || [];
  const overdueCount = useMemo(() => dispatches.filter(isOverdueDispatch).length, [dispatches]);
  const shown = useMemo(() => (overdueOnly ? dispatches.filter(isOverdueDispatch) : dispatches), [dispatches, overdueOnly]);
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const tomorrowKey = format(addDays(new Date(), 1), "yyyy-MM-dd");
  const dispatchToday = useMemo(() => dispatches.filter((d) => d.fulfil_date === todayKey && d.status !== "dispatched"), [dispatches, todayKey]);
  const packTomorrow = useMemo(() => dispatches.filter((d) => d.fulfil_date === tomorrowKey && d.status !== "dispatched"), [dispatches, tomorrowKey]);
  const overdueItems = useMemo(() => dispatches.filter(isOverdueDispatch), [dispatches]);
  const upcomingItems = useMemo(() => dispatches.filter((d) => d.fulfil_date > tomorrowKey && d.status !== "dispatched"), [dispatches, tomorrowKey]);
  const refresh = () => qc.invalidateQueries({ queryKey: ["dispatches"] });

  // Search / status filter spans EVERY dispatch (incl. old, already-dispatched orders) so a rep
  // can go back to a job from weeks ago by site, builder, order # or SKU — independent of tabs.
  // Search spans EVERY dispatch (incl. old, already-dispatched orders) so a rep can go back to a
  // job from weeks ago by site, builder, order # or SKU — independent of the Today/List/Calendar tabs.
  const searching = query.trim().length > 0;
  const terms = useMemo(() => query.trim().toLowerCase().split(/\s+/).filter(Boolean), [query]);
  const results = useMemo(() => {
    if (!searching) return [];
    return dispatches
      .map((d) => matchDispatch(d, terms))
      .filter((r) => r.matched)
      .sort((a, b) => (a.d.fulfil_date < b.d.fulfil_date ? 1 : -1));
  }, [dispatches, terms, searching]);

  // Deep link from push notification: /dispatch?id=<dispatch_id> opens that dispatch.
  useEffect(() => {
    const id = new URLSearchParams(location.search).get("id");
    if (!id || list.isLoading) return;
    const target = dispatches.find((x) => x.id === id);
    if (target) {
      setTab("list");
      setDetailId(id);
    }
    navigate("/dispatch", { replace: true });
  }, [location.search, list.isLoading, dispatches]);

  const lookup = async () => {
    const q = ref.trim();
    if (!q) { toast.error("Enter an invoice / order number"); return; }
    setLoading(true); setData(null);
    try {
      const res = await client.get("/dispatch/lookup", { params: { ref: q } });
      setData(res.data);
      setAddress(res.data.delivery_address || "");
      if (!res.data.items?.length) toast.message("Order found, but it has no line items.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not find that order");
    } finally { setLoading(false); }
  };

  const addToList = async () => {
    if (!data) return;
    if (!fulfilDate) { toast.error(`Pick a ${fulfilType === "pickup" ? "pickup" : "delivery"} date first`); return; }
    if (fulfilType === "delivery") {
      if (!deliveryMethod) { toast.error("Pick a delivery method (NeedAFork, Van or Other)"); return; }
      if (deliveryMethod === "other" && !deliveryMethodOther.trim()) { toast.error("Specify the 'Other' delivery method"); return; }
    }
    setAdding(true);
    try {
      await client.post("/dispatch", {
        ref: data.order_number, fulfil_type: fulfilType, fulfil_date: fulfilDate, handling,
        delivery_address: address, pickup_name: pickupName, pickup_mobile: pickupMobile, warehouse_note: whNote,
        delivery_contact_name: fulfilType === "delivery" ? deliveryContactName : "",
        delivery_contact_mobile: fulfilType === "delivery" ? deliveryContactMobile : "",
        delivery_note: fulfilType === "delivery" ? deliveryNote : "",
        delivery_method: fulfilType === "delivery" ? deliveryMethod : null,
        delivery_method_other: fulfilType === "delivery" && deliveryMethod === "other" ? deliveryMethodOther : null,
      });
      toast.success(`${data.order_number} added to dispatch list`);
      setData(null); setRef(""); setFulfilDate(""); setAddress(""); setPickupName(""); setPickupMobile(""); setFulfilType("delivery"); setWhNote(""); setDeliveryMethod(""); setDeliveryMethodOther(""); setDeliveryContactName(""); setDeliveryContactMobile(""); setDeliveryNote("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to dispatch list");
    } finally { setAdding(false); }
  };

  const setStatus = async (d, status) => {
    try {
      const res = await client.patch(`/dispatch/${d.id}`, { status });
      if (res.data?.advanced_lead) toast.success(`${res.data.advanced_lead.name} moved to Won in the pipeline`);
      refresh();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };

  const removeDispatch = async (d) => {
    if (!window.confirm(`Remove dispatch ${d.order_number} from the list? Uploaded photos will be deleted.`)) return;
    try { await client.delete(`/dispatch/${d.id}`); toast.success("Dispatch removed"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove dispatch"); }
  };

  const viewPdf = async (d) => {
    setPdfBusy(d.id);
    try {
      const res = await client.post(`/dispatch/${d.id}/pdf`, {}, { params: { inline: true }, responseType: "blob", timeout: 90000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      setPdfView({ blob, name: `Dispatch-${d.order_number}.pdf`, dispatch: d });
    } catch (e) {
      toast.error(e?.code === "ECONNABORTED" ? "PDF is taking too long — please try again." : "Could not open PDF. Please try again.");
    }
    finally { setPdfBusy(null); }
  };

  const downloadPdf = async (d) => {
    setPdfBusy(d.id);
    try {
      const res = await client.post(`/dispatch/${d.id}/pdf`, {}, { responseType: "blob", timeout: 90000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Dispatch-${d.order_number}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.code === "ECONNABORTED" ? "PDF is taking too long — please try again." : "Could not generate PDF. Please try again.");
    }
    finally { setPdfBusy(null); }
  };

  const uploadPhotos = async (d, files) => {
    if (!files?.length) return;
    setUploadBusy(d.id);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        await client.post(`/dispatch/${d.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length > 1 ? `${files.length} photos uploaded` : "Photo uploaded");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not upload photo"); }
    finally { setUploadBusy(null); }
  };

  const deletePhoto = async (d, photo) => {
    try { await client.delete(`/dispatch/${d.id}/photos/${photo.id}`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove photo"); }
  };

  const saveDispatch = async (d, fields) => {
    try {
      await client.patch(`/dispatch/${d.id}`, fields);
      toast.success("Dispatch updated");
      refresh();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update dispatch"); return false; }
  };

  const groups = useMemo(() => {
    const map = {};
    shown.filter((d) => d.status !== "dispatched").forEach((d) => { (map[d.fulfil_date] = map[d.fulfil_date] || []).push(d); });
    return Object.keys(map).sort().map((k) => {
      let label = k, overdue = false;
      try {
        const dt = parseISO(k);
        if (isToday(dt)) label = "Today";
        else if (isTomorrow(dt)) label = "Tomorrow";
        else label = format(dt, "EEEE, d MMM yyyy");
        overdue = isPast(startOfDay(dt)) && !isToday(dt);
      } catch { /* keep raw */ }
      return { key: k, label, overdue, items: map[k] };
    });
  }, [shown]);

  const completed = useMemo(
    () => dispatches.filter((d) => d.status === "dispatched").sort((a, b) => (a.fulfil_date < b.fulfil_date ? 1 : -1)),
    [dispatches]
  );

  const byDate = useMemo(() => {
    const acc = {};
    shown.forEach((d) => { (acc[d.fulfil_date] = acc[d.fulfil_date] || []).push(d); });
    return acc;
  }, [shown]);

  const gridDays = eachDayOfInterval({ start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }) });
  const selKey = format(selected, "yyyy-MM-dd");
  const selectedItems = byDate[selKey] || [];

  const cardProps = { onStatus: setStatus, onDelete: removeDispatch, onPdf: downloadPdf, onView: viewPdf, onOpenPhotos: (d) => setDetailId(d.id), canManage: canAdd };
  const detail = useMemo(() => dispatches.find((x) => x.id === detailId) || null, [dispatches, detailId]);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Dispatch" subtitle="Fetch an inFlow order, schedule it and track dispatch with photo proof" testid="dispatch-header" />

      {canAdd && (
      <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
        <Label className="mb-1.5 block">Invoice / order number</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input value={ref} onChange={(e) => setRef(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") lookup(); }} placeholder="e.g. INV-20062026-06" data-testid="dispatch-ref-input" className="font-mono" />
          <Button onClick={lookup} disabled={loading} data-testid="dispatch-lookup-btn" className="gap-2 sm:w-40">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Look up
          </Button>
        </div>

        {data && (
          <div className="animate-fade-up mt-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-accent px-3 py-1 font-mono text-sm font-bold text-accent-foreground" data-testid="dispatch-order-number">{data.order_number}</span>
              {data.company && <span className="text-sm font-semibold text-ink">{data.company}</span>}
              <span className="text-xs text-muted-foreground">{data.item_count} item(s)</span>
            </div>

            <Label className="mt-4 mb-1.5 flex items-center gap-1.5 text-xs"><CalendarDays className="h-3.5 w-3.5" />Fulfilment</Label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="dispatch-fulfil-toggle">
                <button type="button" onClick={() => setFulfilType("delivery")} data-testid="dispatch-type-delivery"
                  className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", fulfilType === "delivery" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}>
                  <Truck className="h-3.5 w-3.5" />Delivery
                </button>
                <button type="button" onClick={() => setFulfilType("pickup")} data-testid="dispatch-type-pickup"
                  className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", fulfilType === "pickup" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}>
                  <Store className="h-3.5 w-3.5" />Customer pickup
                </button>
              </div>
              <DateField value={fulfilDate} onChange={setFulfilDate} testid="dispatch-date-input" className="w-full sm:w-56" />
            </div>

            {fulfilType === "delivery" && <MethodSelect value={deliveryMethod} other={deliveryMethodOther} onValue={setDeliveryMethod} onOther={setDeliveryMethodOther} idp="dispatch-" />}

            <Label className="mt-4 mb-1.5 flex items-center gap-1.5 text-xs"><Package className="h-3.5 w-3.5" />Handling for warehouse</Label>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="dispatch-handling-toggle">
              <button type="button" onClick={() => setHandling("hand")} data-testid="dispatch-handling-hand" className={cn("rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", handling === "hand" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}>Hand loaded</button>
              <button type="button" onClick={() => setHandling("pallet")} data-testid="dispatch-handling-pallet" className={cn("rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", handling === "pallet" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}>Pallet</button>
            </div>

            <Label className="mt-4 mb-1.5 flex items-center gap-1.5 text-xs"><Package className="h-3.5 w-3.5" />Note for warehouse picking team</Label>
            <Textarea value={whNote} onChange={(e) => setWhNote(e.target.value)} placeholder="e.g. Fragile — double-box tapware. Customer collecting 2pm, load tailgate side." data-testid="dispatch-warehouse-note" className="min-h-[60px] bg-white" />
            <p className="mt-1 text-[11px] text-muted-foreground">Appears as a highlighted "Picking notes" box on the dispatch PDF.</p>

            {fulfilType === "pickup" ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="mb-1.5 flex items-center gap-1.5 text-xs"><User className="h-3.5 w-3.5" />Pickup person name</Label>
                  <Input value={pickupName} onChange={(e) => setPickupName(e.target.value)} placeholder="Who is collecting the order?" data-testid="dispatch-pickup-name-input" className="bg-white" />
                </div>
                <div>
                  <Label className="mb-1.5 flex items-center gap-1.5 text-xs"><Phone className="h-3.5 w-3.5" />Pickup mobile</Label>
                  <Input value={pickupMobile} onChange={(e) => setPickupMobile(e.target.value)} placeholder="Contact number" inputMode="tel" data-testid="dispatch-pickup-mobile-input" className="bg-white" />
                </div>
              </div>
            ) : (
              <>
                <Label className="mt-4 mb-1.5 flex items-center gap-1.5 text-xs"><MapPin className="h-3.5 w-3.5" />Delivery address</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Add the delivery address (blank on the inFlow order)" data-testid="dispatch-address-input" className="bg-white" />
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1.5 flex items-center gap-1.5 text-xs"><User className="h-3.5 w-3.5" />Customer name <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Input value={deliveryContactName} onChange={(e) => setDeliveryContactName(e.target.value)} placeholder="On-site contact name" data-testid="dispatch-delivery-name-input" className="bg-white" />
                  </div>
                  <div>
                    <Label className="mb-1.5 flex items-center gap-1.5 text-xs"><Phone className="h-3.5 w-3.5" />Customer number <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Input value={deliveryContactMobile} onChange={(e) => setDeliveryContactMobile(e.target.value)} placeholder="Contact number" inputMode="tel" data-testid="dispatch-delivery-mobile-input" className="bg-white" />
                  </div>
                </div>
                <Label className="mt-4 mb-1.5 flex items-center gap-1.5 text-xs"><Package className="h-3.5 w-3.5" />Delivery note <span className="font-normal text-muted-foreground">(optional)</span></Label>
                <Textarea value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="e.g. Call on arrival, gate code 1234, leave at side entrance." data-testid="dispatch-delivery-note-input" className="min-h-[60px] bg-white" />
                <p className="mt-1 text-[11px] text-muted-foreground">Shown to the delivery partner on the shared delivery link.</p>
              </>
            )}

            <div className="mt-4 flex justify-end">
              <Button onClick={addToList} disabled={adding} data-testid="add-to-dispatch-btn" className="gap-2">
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to Dispatch List
              </Button>
            </div>
          </div>
        )}
      </div>
      )}

      {canAdd && (needs.data || []).length > 0 && (
        <div className="mb-4 rounded-2xl border border-[#D97706]/30 bg-[#D97706]/5 p-4" data-testid="needs-scheduling-banner">
          <button onClick={() => setNeedsOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 text-left" data-testid="needs-scheduling-toggle">
            <span className="flex items-center gap-2 text-sm font-bold text-warning"><AlertTriangle className="h-4 w-4" />{needs.data.length} invoiced builder {needs.data.length === 1 ? "stage" : "stages"} not yet on the dispatch schedule</span>
            <span className="shrink-0 text-xs font-semibold text-warning">{needsOpen ? "Hide" : "Review"}</span>
          </button>
          {needsOpen && (
            <div className="mt-3 space-y-1.5">
              {needs.data.map((n) => (
                <div key={n.order_number} className="flex items-center justify-between gap-3 rounded-lg border border-[#D97706]/20 bg-white px-3 py-2 text-sm" data-testid={`needs-row-${n.order_number}`}>
                  <span className="min-w-0 truncate"><span className="font-semibold text-ink">{n.builder_name || "Builder"}</span><span className="text-muted-foreground"> — {n.job_title}{n.stage_label ? ` · ${n.stage_label}` : ""} · {n.order_number}</span></span>
                  <button onClick={() => window.location.assign(`/builders/${n.builder_id}`)} className="shrink-0 text-xs font-semibold text-primary hover:underline" data-testid={`needs-open-${n.order_number}`}>Open site</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-4" data-testid="dispatch-search-row">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="dispatch-search-input"
            placeholder="Search dispatches — builder, site/address, order #, SKU, note…" className="pl-9 pr-9" />
          {query && (
            <button onClick={() => setQuery("")} data-testid="dispatch-search-clear" title="Clear search"
              className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-ink">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {!searching && (
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="dispatch-view-tabs">
          <button onClick={() => setTab("today")} data-testid="tab-today" className={cn("inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors", tab === "today" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}><Truck className="h-4 w-4" />Today</button>
          <button onClick={() => setTab("list")} data-testid="tab-list" className={cn("inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors", tab === "list" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}><ListChecks className="h-4 w-4" />List</button>
          <button onClick={() => setTab("calendar")} data-testid="tab-calendar" className={cn("inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors", tab === "calendar" ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-ink")}><CalendarDays className="h-4 w-4" />Calendar</button>
        </div>
        {tab !== "today" && (
          <button onClick={() => setOverdueOnly((v) => !v)} data-testid="overdue-filter-toggle"
            className={cn("inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors", overdueOnly ? "border-error bg-error/10 text-error" : "border-slate-200 bg-white text-muted-foreground hover:text-ink")}>
            <AlertTriangle className="h-4 w-4" />Overdue only
            {overdueCount > 0 && <span className={cn("rounded-full px-1.5 text-xs font-bold", overdueOnly ? "bg-error/20" : "bg-error/10 text-error")}>{overdueCount}</span>}
          </button>
        )}
        <div className="ml-auto"><PushBell /></div>
      </div>
      )}

      {list.isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-muted/50" />
      ) : dispatches.length === 0 ? (
        <EmptyState icon={PackageSearch} title="No dispatches yet" hint={canAdd ? "Look up an inFlow order above and add it to the dispatch list to start planning." : "No dispatches have been scheduled yet."} />
      ) : searching ? (
        <div className="space-y-3" data-testid="dispatch-search-results">
          <p className="text-sm text-muted-foreground" data-testid="dispatch-search-count">
            {results.length} {results.length === 1 ? "match" : "matches"}{query.trim() ? <> for "<span className="font-semibold text-ink">{query.trim()}</span>"</> : ""}
          </p>
          {results.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-10 text-center text-sm font-medium text-muted-foreground" data-testid="dispatch-search-empty">
              No dispatches match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {results.map((r) => (
                <div key={r.d.id}>
                  <DispatchCard d={r.d} {...cardProps} terms={terms} busyPdf={pdfBusy === r.d.id} uploading={uploadBusy === r.d.id} />
                  {r.hints.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5 px-1" data-testid={`search-hints-${r.d.id}`}>
                      {r.hints.slice(0, 3).map((h, i) => (
                        <span key={i} className="inline-flex max-w-[260px] items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-foreground">
                          <span className="font-semibold">{h.label}:</span><span className="truncate"><Highlight text={h.text} terms={terms} /></span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : tab === "today" ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="dispatch-today-board">
          <BoardColumn testid="board-dispatch-today" title="Dispatch today" Icon={Truck} items={dispatchToday}
            headerCls="bg-primary/10 text-primary" emptyText="Nothing to send out today." cardProps={cardProps} pdfBusy={pdfBusy} uploadBusy={uploadBusy} />
          <BoardColumn testid="board-pack-tomorrow" title="Pack for tomorrow" Icon={Package} items={packTomorrow}
            headerCls="bg-[#D97706]/12 text-[#D97706]" emptyText="Nothing to prep for tomorrow." cardProps={cardProps} pdfBusy={pdfBusy} uploadBusy={uploadBusy} />
          <BoardColumn testid="board-overdue" title="Overdue" Icon={AlertTriangle} items={overdueItems}
            headerCls="bg-error/10 text-error" emptyText="All caught up 🎉" cardProps={cardProps} pdfBusy={pdfBusy} uploadBusy={uploadBusy} />
          <BoardColumn testid="board-upcoming" title="Upcoming" Icon={CalendarDays} items={upcomingItems}
            headerCls="bg-slate-200/70 text-slate-600" emptyText="Nothing scheduled further out." cardProps={cardProps} pdfBusy={pdfBusy} uploadBusy={uploadBusy} />
        </div>
      ) : tab === "list" ? (
        <div className="space-y-6" data-testid="dispatch-list">
          {overdueOnly && shown.length === 0 && (
            <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/50 py-10 text-center text-sm font-medium text-emerald-700">All caught up — no overdue dispatches 🎉</div>
          )}
          {!overdueOnly && groups.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border py-10 text-center text-sm font-medium text-muted-foreground" data-testid="no-active-dispatches">
              No active dispatches to prep.{completed.length > 0 ? " Completed orders are in the section below." : ""}
            </div>
          )}
          {groups.map((g) => (
            <div key={g.key}>
              <div className="mb-2 flex items-center gap-2">
                <h3 className={cn("flex items-center gap-1.5 font-display text-sm font-bold", g.overdue ? "text-error" : "text-ink")}>
                  {g.overdue && <AlertTriangle className="h-3.5 w-3.5" />}{g.label}{g.overdue ? " · Overdue" : ""}
                </h3>
                <span className="text-xs text-muted-foreground">· {g.items.length}</span>
              </div>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {g.items.map((d) => <DispatchCard key={d.id} d={d} {...cardProps} busyPdf={pdfBusy === d.id} uploading={uploadBusy === d.id} />)}
              </div>
            </div>
          ))}
          {!overdueOnly && completed.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/50">
              <button onClick={() => setShowCompleted((v) => !v)} data-testid="toggle-completed"
                className="flex w-full items-center gap-2 px-4 py-3 text-sm font-bold text-ink transition-colors hover:bg-slate-100/60">
                <CheckCircle2 className="h-4 w-4 text-[#059669]" />
                Dispatched
                <span className="rounded-full bg-[#059669]/12 px-2 py-0.5 text-xs font-bold text-[#059669]">{completed.length}</span>
                <ChevronRight className={cn("ml-auto h-4 w-4 text-muted-foreground transition-transform", showCompleted && "rotate-90")} />
              </button>
              {showCompleted && (
                <div className="grid grid-cols-1 gap-3 border-t border-slate-100 p-4 lg:grid-cols-2" data-testid="completed-list">
                  {completed.map((d) => <DispatchCard key={d.id} d={d} {...cardProps} busyPdf={pdfBusy === d.id} uploading={uploadBusy === d.id} />)}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3" data-testid="dispatch-calendar">
          <div className="rounded-2xl border border-border bg-white p-4 shadow-[0_2px_8px_rgba(35,52,90,0.04)] lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-ink">{format(month, "MMMM yyyy")}</h2>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={() => setMonth(subMonths(month, 1))} data-testid="disp-cal-prev" className="h-8 w-8"><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => { setMonth(new Date()); setSelected(new Date()); }} data-testid="disp-cal-today">Today</Button>
                <Button variant="outline" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="disp-cal-next" className="h-8 w-8"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {gridDays.map((day) => {
                const key = format(day, "yyyy-MM-dd");
                const items = byDate[key] || [];
                const deliveries = items.filter((x) => x.fulfil_type !== "pickup").length;
                const pickups = items.filter((x) => x.fulfil_type === "pickup").length;
                const inMonth = isSameMonth(day, month);
                const isSel = isSameDay(day, selected);
                return (
                  <button key={key} onClick={() => setSelected(day)} data-testid={`disp-cal-day-${key}`}
                    className={cn("flex min-h-[64px] flex-col rounded-lg border p-1.5 text-left transition-colors", isSel ? "border-primary bg-accent" : "border-transparent hover:border-border hover:bg-muted/50", !inMonth && "opacity-40")}>
                    <span className={cn("text-xs font-semibold", isToday(day) ? "grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground" : "text-ink")}>{format(day, "d")}</span>
                    <div className="mt-auto flex flex-wrap gap-1">
                      {deliveries > 0 && <span className="inline-flex items-center gap-0.5 rounded px-1 text-[10px] font-semibold text-primary bg-primary/10"><Truck className="h-2.5 w-2.5" />{deliveries}</span>}
                      {pickups > 0 && <span className="inline-flex items-center gap-0.5 rounded px-1 text-[10px] font-semibold text-[#7C3AED] bg-[#7C3AED]/12"><Store className="h-2.5 w-2.5" />{pickups}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground" data-testid="disp-cal-legend">
              <span className="flex items-center gap-1.5"><span className="grid h-4 w-4 place-items-center rounded bg-primary/10 text-primary"><Truck className="h-2.5 w-2.5" /></span>Delivery</span>
              <span className="flex items-center gap-1.5"><span className="grid h-4 w-4 place-items-center rounded bg-[#7C3AED]/12 text-[#7C3AED]"><Store className="h-2.5 w-2.5" /></span>Customer pickup</span>
            </div>
          </div>
          <div className="lg:col-span-1">
            <h3 className="mb-1 font-display text-base font-bold text-ink">{format(selected, "EEEE, d MMM")}</h3>
            <p className="mb-3 text-xs text-muted-foreground">{selectedItems.length} {selectedItems.length === 1 ? "dispatch" : "dispatches"}</p>
            {selectedItems.length === 0 ? (
              <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-10 text-center">
                <CalendarDays className="mb-2 h-6 w-6 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Nothing scheduled for this day.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedItems.map((d) => <DispatchCard key={d.id} d={d} {...cardProps} busyPdf={pdfBusy === d.id} uploading={uploadBusy === d.id} />)}
              </div>
            )}
          </div>
        </div>
      )}

      <PdfViewer
        open={!!pdfView}
        onClose={() => setPdfView(null)}
        blob={pdfView?.blob}
        title={pdfView?.name || "Dispatch PDF"}
        onDownload={pdfView ? () => downloadPdf(pdfView.dispatch) : undefined}
      />

      <DispatchDetailDialog
        dispatch={detail}
        onClose={() => setDetailId(null)}
        onStatus={setStatus}
        onUpload={uploadPhotos}
        onDeletePhoto={deletePhoto}
        onSave={saveDispatch}
        canManage={canAdd}
        uploading={uploadBusy === detailId}
      />
    </div>
  );
}
